import { ElementPropertiesVisualizationMode } from '../models/element-properties.model';

/**
 * Result of merging the property views of several selected elements.
 * `view` holds the keys common to every element; a value is kept when every
 * element agrees on it, otherwise the first element's value is kept only as a
 * type sample and the dotted path is listed in `mixedPaths`.
 */
export interface MergedElementProperties {
  view: ElementPropertiesVisualizationMode
  mixedPaths: Set<string>
}

/** Top-level groups merged key-by-key; every other key comes from the primary view. */
const MERGED_GROUPS: (keyof ElementPropertiesVisualizationMode)[] = ['General', 'Material', 'StructureAssessment', 'TypicalDetails']

export class MultiElementPropertiesMapper {

  public static Merge(views: ElementPropertiesVisualizationMode[]): MergedElementProperties {
    const mixedPaths = new Set<string>()
    if (!views || views.length === 0) {
      return { view: null, mixedPaths }
    }
    const primary = views[0]
    if (views.length === 1) {
      return { view: primary, mixedPaths }
    }

    const view: any = { ...primary }
    for (const group of MERGED_GROUPS) {
      const groups = views.map(v => v?.[group])
      if (groups.some(g => g == null || typeof g !== 'object')) {
        delete view[group]
        continue
      }
      view[group] = MultiElementPropertiesMapper.mergeGroup(group, groups, mixedPaths)
    }
    return { view, mixedPaths }
  }

  private static mergeGroup(groupName: string, groups: any[], mixedPaths: Set<string>): any {
    const commonKeys = Object.keys(groups[0]).filter(key => groups.every(g => key in g))
    const merged: any = {}
    for (const key of commonKeys) {
      const sample = groups[0][key]
      const allEqual = groups.every(g => MultiElementPropertiesMapper.ValuesEqual(g[key], sample))
      merged[key] = sample
      if (!allEqual) {
        mixedPaths.add(groupName + '.' + key)
      }
    }
    return merged
  }

  /** Loose equality that tolerates number-vs-formatted-string differences produced by the per-element mapper. */
  public static ValuesEqual(a: any, b: any): boolean {
    if (a === b) return true
    if (a == null || b == null) return a == b
    if (typeof a === 'boolean' || typeof b === 'boolean') return a === b
    const na = Number(a)
    const nb = Number(b)
    if (a !== '' && b !== '' && Number.isFinite(na) && Number.isFinite(nb)) {
      return na === nb
    }
    return String(a) === String(b)
  }
}
